import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import PageMeta from '../components/PageMeta';
import api from '../services/api';
import { getVideoSource } from '../utils/video';
import './BlogDetail.css';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' });
};

const BlogDetail = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await api.get(`/blogs/${id}`);
        setPost(response.data);
      } catch (err) {
        console.error('Failed to fetch blog', err);
        setError(err.response?.data?.message || 'Failed to load post.');
        setPost(null);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [id]);

  if (loading) {
    return (
      <Layout>
        <div className="blog-detail-page">
          <div className="blog-detail-container">
            <p className="blog-detail-loading">Loading…</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !post) {
    return (
      <Layout>
        <div className="blog-detail-page">
          <div className="blog-detail-container">
            <p className="blog-detail-error">{error || 'Post not found.'}</p>
            <Link to="/blog" className="blog-detail-back">← Back to Blog</Link>
          </div>
        </div>
      </Layout>
    );
  }

  const videoSource = getVideoSource(post.video_url);

  return (
    <Layout>
      <PageMeta
        title={post.title}
        description={post.content ? post.content.replace(/<[^>]+>/g, ' ').slice(0, 160) + '…' : 'Blog post'}
      />
      <div className="blog-detail-page">
        <article className="blog-detail-container">
          <Link to="/blog" className="blog-detail-back">← Back to Blog</Link>

          <header className="blog-detail-header">
            <time dateTime={post.blog_date} className="blog-detail-date">
              {formatDate(post.blog_date)}
            </time>
            <h1 className="blog-detail-title">{post.title}</h1>
          </header>

          {post.featured_image_url && (
            <div className="blog-detail-media blog-detail-image">
              <img src={post.featured_image_url} alt="" />
            </div>
          )}

          {videoSource && (
            <div className="blog-detail-media blog-detail-video">
              {videoSource.type === 'file' ? (
                <video src={videoSource.url} controls playsInline className="blog-detail-video-el" />
              ) : (
                <iframe
                  title="Blog video"
                  src={videoSource.url}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  className="blog-detail-iframe"
                />
              )}
            </div>
          )}

          <div
            className="blog-detail-content"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {post.external_link && (
            <p className="blog-detail-external">
              <a href={post.external_link} target="_blank" rel="noopener noreferrer" className="blog-detail-external-link">
                External link →
              </a>
            </p>
          )}
        </article>
      </div>
    </Layout>
  );
};

export default BlogDetail;
